import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Mail,
  CheckCircle2,
  Check,
  Truck,
  ShieldCheck,
  AlertCircle,
  Loader2,
  MapPin,
  User,
  Banknote,
  Lock,
  RotateCcw,
  Eye,
  EyeOff,
  UserCheck,
  ShoppingBag,
  PackageCheck,
  Copy,
  ChevronDown,
  ChevronUp,
  KeyRound,
  Clock,
  Sparkles,
  HelpCircle,
  FileText,
  ExternalLink,
  Phone,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import * as locationsApi from "../api/locationsApi";
import * as guestApi from "../api/guestApi";
import { toGuestCheckoutItems } from "../utils/guestCart";
import { formatMoney, formatProductSize, normalizeEgyptianPhone, validateEgyptianPhone } from "../utils/format";
import { resolveImageUrl } from "../utils/images";
import SearchableSelect from "../components/SearchableSelect";
import CouponBox from "../components/CouponBox";
import "./AccountForms.css";
import "./Checkout.css";
import "./Auth.css";
import "./Thanks.css";
import "./GuestConfirmation.css";



export default function GuestCheckout() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t, isRTL, language } = useLanguage();
  const {
    guestCart,
    subtotal,
    shipping,
    total,
    clearGuestCart,
    appliedCoupon,
    discountAmount,
    removeCoupon,
    isOverPackageLimit,
    maxPackageVolume,
  } = useCart();

  // Contact info
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Address info
  const [country] = useState("Egypt");
  const [cityId, setCityId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [description, setDescription] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("CashOnDelivery");

  // UI state
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderPlacedInfo, setOrderPlacedInfo] = useState(null);

  const { login } = useAuth();

  // Secure email link-based account setup state
  const [setupLinkSent, setSetupLinkSent] = useState(false);
  const [setupLinkLoading, setSetupLinkLoading] = useState(false);
  const [setupLinkCooldown, setSetupLinkCooldown] = useState(0);
  const [setupLinkError, setSetupLinkError] = useState("");
  const [copiedOrderId, setCopiedOrderId] = useState(false);
  const [isResetInfoExpanded, setIsResetInfoExpanded] = useState(false);

  // Countdown timer for resending setup link
  useEffect(() => {
    if (setupLinkCooldown <= 0) return;
    const timer = setInterval(() => {
      setSetupLinkCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [setupLinkCooldown]);

  // Summary & dynamic Bosta shipping fee state
  const [checkoutSummary, setCheckoutSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Load cities on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingCities(true);
    locationsApi
      .getCities()
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setCities(data);
        }
      })
      .catch((err) => {
        console.error("Could not load cities:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Load districts when city changes
  useEffect(() => {
    setDistricts([]);
    setDistrictId("");
    setCheckoutSummary(null);

    if (!cityId) {
      return;
    }

    let cancelled = false;
    setLoadingDistricts(true);
    locationsApi
      .getDistricts(cityId)
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setDistricts(data);
        }
      })
      .catch((err) => {
        console.error("Could not load districts:", err);
        if (!cancelled) setDistricts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingDistricts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cityId]);

  // Load checkout summary & real-time Bosta shipping fees whenever district or cart changes
  useEffect(() => {
    if (!cityId || !districtId) {
      setCheckoutSummary(null);
      return;
    }

    // Ensure the district actually belongs to the loaded districts for the current city
    const isValidDistrict = districts.some((d) => d.id === districtId);
    if (!isValidDistrict) {
      setCheckoutSummary(null);
      return;
    }

    const items = toGuestCheckoutItems(guestCart);
    if (items.length === 0) {
      setCheckoutSummary(null);
      return;
    }

    let cancelled = false;
    setLoadingSummary(true);

    guestApi
      .getGuestCheckoutSummary({
        address: {
          country,
          cityId,
          districtId,
        },
        paymentMethod,
        couponCode: appliedCoupon?.code || undefined,
        items,
      })
      .then((data) => {
        if (!cancelled && data) {
          setCheckoutSummary(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Could not load guest checkout summary:", err);
          const errorMsg =
            err?.message || "Failed to calculate shipping fees for this location.";
          addToast(errorMsg, "error");
          setCheckoutSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [districtId, districts, cityId, paymentMethod, guestCart, country, appliedCoupon?.code, addToast]);

  const currentSubtotal = checkoutSummary?.cart?.totalPrice ?? subtotal;
  const effectiveDiscount = discountAmount || Number(checkoutSummary?.discountAmount || 0);
  const calculatedShipping = checkoutSummary ? Number(checkoutSummary.shippingFees || 0) : null;
  const currentTotal = calculatedShipping != null 
    ? Math.max(0, currentSubtotal - effectiveDiscount) + calculatedShipping 
    : Math.max(0, currentSubtotal - effectiveDiscount);




  const handleSubmit = async (e) => {
    e.preventDefault();

    if (guestCart.length === 0) {
      addToast(t("guestCheckout.emptyDesc"), "error");
      return;
    }

    if (isOverPackageLimit) {
      addToast(t("cart.mustReduceItemsToCheckout"), "error");
      return;
    }

    // Email validation
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      addToast(
        isRTL
          ? "يرجى إدخال عنوان بريد إلكتروني صالح."
          : "Please provide a valid email address.",
        "error"
      );
      return;
    }

    // Name validation
    if (!firstName.trim() || !lastName.trim()) {
      addToast(
        isRTL
          ? "يرجى إدخال الاسم الأول واسم العائلة."
          : "Please enter your first and last name.",
        "error"
      );
      return;
    }

    // City & District validation
    if (!cityId || !districtId) {
      addToast(t("guestCheckout.errCity"), "error");
      return;
    }

    // Address description validation (backend requires min. 25 characters)
    const trimmedDesc = description.trim();
    if (trimmedDesc.length < 25) {
      addToast(t("guestCheckout.errDescMin"), "error");
      return;
    }

    // Phone number validation & normalization (e.g. 010xxxxxxxx or 10xxxxxxxx -> 201022334455)
    if (!validateEgyptianPhone(phoneNumber)) {
      addToast(t("guestCheckout.errPhone"), "error");
      return;
    }
    const formattedPhone = normalizeEgyptianPhone(phoneNumber);

    // Format items for GuestAddItemToCartDto
    const items = toGuestCheckoutItems(guestCart);
    if (items.length === 0) {
      addToast(t("guestCheckout.errVariants"), "error");
      return;
    }

    const payload = {
      email: trimmedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      shippingAddress: {
        country: country,
        cityId,
        districtId,
        description: trimmedDesc,
        phoneNumber: formattedPhone,
        isDefault: true,
      },
      paymentMethod,
      couponCode: appliedCoupon?.code || undefined,
      items,
    };

    setSubmitting(true);
    try {
      const res = await guestApi.guestCheckout(payload);

      // Successfully submitted
      setOrderPlacedInfo({
        email: trimmedEmail,
        name: `${firstName.trim()} ${lastName.trim()}`,
        phone: formattedPhone,
        orderId: res?.orderId || res?.OrderId || null,
        url: res?.url || res?.Url || "",
      });

      // Clear the local guest cart & coupon
      clearGuestCart();
      removeCoupon();
      addToast(t("guestCheckout.successToast"), "success");
    } catch (err) {
      addToast(err.message || t("guestCheckout.errFailed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyOrderId = () => {
    const id = orderPlacedInfo?.orderId;
    if (!id) return;
    navigator.clipboard.writeText(String(id)).then(() => {
      setCopiedOrderId(true);
      setTimeout(() => setCopiedOrderId(false), 2200);
    });
  };

  const handleRequestSetupLink = async () => {
    if (!orderPlacedInfo?.email || setupLinkLoading || setupLinkCooldown > 0) return;

    setSetupLinkLoading(true);
    setSetupLinkError("");
    try {
      await guestApi.requestGuestPasswordSetup(orderPlacedInfo.email);
      setSetupLinkSent(true);
      setSetupLinkCooldown(60);
      addToast(
        isRTL
          ? "تم إرسال رابط إنشاء كلمة المرور إلى بريدك الإلكتروني بنجاح!"
          : "Password setup link sent to your email successfully!",
        "success"
      );
    } catch (err) {
      const msg = err.message || (isRTL ? "فشل إرسال الرابط. يرجى المحاولة لاحقاً." : "Failed to send link.");
      setSetupLinkError(msg);
      addToast(msg, "error");
    } finally {
      setSetupLinkLoading(false);
    }
  };

  // SUCCESS CONFIRMATION STATE - ELEGANT CELEBRATION & INTERACTIVE ACCOUNT TRACKING
  if (orderPlacedInfo) {
    return (
      <div className="checkout-page guest-confirm-page">
        <div className="container guest-confirm-inner">
          {/* Checkout Step Breadcrumbs indicating Step 3: Done */}
          <nav className="cart-steps" aria-label="Checkout flow">
            <Link
              to="/cart"
              className="cart-step cart-step--completed"
              title={t("checkout.backToCart")}
            >
              <span className="cart-step__num">
                <Check size={13} strokeWidth={2.5} />
              </span>
              <span className="cart-step__label">{t("cart.stepBag")}</span>
            </Link>
            <span className="cart-steps__divider" />
            <div className="cart-step cart-step--completed">
              <span className="cart-step__num">
                <Check size={13} strokeWidth={2.5} />
              </span>
              <span className="cart-step__label">{t("cart.stepCheckout")}</span>
            </div>
            <span className="cart-steps__divider" />
            <div className="cart-step cart-step--active" aria-current="step">
              <span className="cart-step__num">
                <Check size={13} strokeWidth={2.5} />
              </span>
              <span className="cart-step__label">{t("cart.stepDone")}</span>
            </div>
          </nav>

          {/* 1. Hero Confirmation Card */}
          <section className="guest-confirm-hero">
            <div className="guest-confirm-hero__seal">
              <div className="guest-confirm-hero__icon-wrap">
                <CheckCircle2 size={46} strokeWidth={2.2} />
              </div>
              <div className="guest-confirm-hero__glow" />
            </div>

            <span className="guest-confirm-hero__eyebrow">
              <ShieldCheck size={14} />
              <span>{t("brand.name")} • {t("guestCheckout.orderConfirmedBadge")}</span>
            </span>

            <h1 className="guest-confirm-hero__title">{t("guestCheckout.successTitle")}</h1>

            <p className="guest-confirm-hero__subtitle">
              {t("guestCheckout.successHeroSub")
                .replace("{name}", orderPlacedInfo.name || firstName)
                .replace("{email}", orderPlacedInfo.email)}
            </p>

            {/* Order Reference Box with 1-click Copy */}
            {orderPlacedInfo.orderId && (
              <div className="guest-ref-box">
                <div className="guest-ref-box__meta">
                  <span className="guest-ref-box__label">{t("guestCheckout.orderReference")}</span>
                  <span className="guest-ref-box__code" title={orderPlacedInfo.orderId}>
                    #{orderPlacedInfo.orderId}
                  </span>
                </div>
                <button
                  type="button"
                  className={`guest-ref-box__copy ${copiedOrderId ? "guest-ref-box__copy--copied" : ""}`}
                  onClick={handleCopyOrderId}
                  aria-label={copiedOrderId ? t("guestCheckout.copied") : t("guestCheckout.copy")}
                >
                  {copiedOrderId ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copiedOrderId ? t("guestCheckout.copied") : t("guestCheckout.copy")}</span>
                </button>
              </div>
            )}

            {/* Quick Meta Pills */}
            <div className="guest-confirm-pills">
              <div className="guest-confirm-pill">
                <Clock size={16} />
                <div>
                  <span className="guest-confirm-pill__label">{t("guestCheckout.deliveryEstimate")}</span>
                  <strong className="guest-confirm-pill__val">{t("guestCheckout.deliveryEstimateVal")}</strong>
                </div>
              </div>

              <div className="guest-confirm-pill">
                <Truck size={16} />
                <div>
                  <span className="guest-confirm-pill__label">{t("guestCheckout.courierLabel")}</span>
                  <strong className="guest-confirm-pill__val">{t("guestCheckout.courierName")}</strong>
                </div>
              </div>

              <div className="guest-confirm-pill">
                <Banknote size={16} />
                <div>
                  <span className="guest-confirm-pill__label">{t("guestCheckout.paymentLabel")}</span>
                  <strong className="guest-confirm-pill__val">
                    {paymentMethod === "CreditCard"
                      ? t("guestCheckout.paymentCardVal")
                      : t("guestCheckout.paymentCodVal")}
                  </strong>
                </div>
              </div>

              <div className="guest-confirm-pill">
                <User size={16} />
                <div>
                  <span className="guest-confirm-pill__label">{t("guestCheckout.recipientLabel")}</span>
                  <strong className="guest-confirm-pill__val">
                    {orderPlacedInfo.name}
                    {orderPlacedInfo.phone ? ` • ${orderPlacedInfo.phone}` : ""}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Order & Shipment Progress Timeline */}
          <section className="guest-timeline-card thanks-timeline-card">
            <h2 className="guest-timeline-title">
              <Truck size={18} />
              <span>{t("guestCheckout.timelineTitle")}</span>
            </h2>
            <div className="thanks-timeline">
              <div className="thanks-timeline__step thanks-timeline__step--completed">
                <div className="thanks-timeline__node">
                  <Check size={15} />
                </div>
                <div className="thanks-timeline__content">
                  <span className="thanks-timeline__step-title">{t("guestCheckout.step1Title")}</span>
                  <span className="thanks-timeline__step-desc">{t("guestCheckout.step1Desc")}</span>
                </div>
              </div>

              <div className="thanks-timeline__step thanks-timeline__step--active">
                <div className="thanks-timeline__node">
                  <PackageCheck size={15} />
                </div>
                <div className="thanks-timeline__content">
                  <span className="thanks-timeline__step-title">{t("guestCheckout.step2Title")}</span>
                  <span className="thanks-timeline__step-desc">{t("guestCheckout.step2Desc")}</span>
                </div>
              </div>

              <div className="thanks-timeline__step">
                <div className="thanks-timeline__node">
                  <Truck size={15} />
                </div>
                <div className="thanks-timeline__content">
                  <span className="thanks-timeline__step-title">{t("guestCheckout.step3Title")}</span>
                  <span className="thanks-timeline__step-desc">{t("guestCheckout.step3Desc")}</span>
                </div>
              </div>

              <div className="thanks-timeline__step">
                <div className="thanks-timeline__node">
                  <ShieldCheck size={15} />
                </div>
                <div className="thanks-timeline__content">
                  <span className="thanks-timeline__step-title">{t("guestCheckout.step4Title")}</span>
                  <span className="thanks-timeline__step-desc">{t("guestCheckout.step4Desc")}</span>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Account Tracking & Secure Email Activation Section */}
          <section className="guest-account-card">
            <div>
              <div className="guest-account-header">
                <div className="guest-account-header__icon-badge">
                  <KeyRound size={24} />
                </div>
                <div className="guest-account-header__body">
                  <span className="guest-account-header__badge">
                    <Sparkles size={12} />
                    <span>{t("guestCheckout.trackAccountSectionBadge")}</span>
                  </span>
                  <h3 className="guest-account-header__title">{t("guestCheckout.trackAccountTitle")}</h3>
                  <p className="guest-account-header__desc">
                    {t("guestCheckout.trackAccountSub").replace("{email}", orderPlacedInfo.email)}
                  </p>

                  <div className="guest-account-perks">
                    <span className="guest-account-perk">
                      <PackageCheck size={14} />
                      <span>{t("guestCheckout.featureLiveTrack")}</span>
                    </span>
                    <span className="guest-account-perk">
                      <FileText size={14} />
                      <span>{t("guestCheckout.featureInvoices")}</span>
                    </span>
                    <span className="guest-account-perk">
                      <ShieldCheck size={14} />
                      <span>{t("guestCheckout.featureQuickCheckout")}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Secure Link Action & Confirmation Box */}
              {!setupLinkSent ? (
                <div className="guest-send-link-wrap">
                  <button
                    type="button"
                    className="btn-primary guest-send-link-btn"
                    onClick={handleRequestSetupLink}
                    disabled={setupLinkLoading}
                  >
                    {setupLinkLoading ? (
                      <>
                        <Loader2
                          size={18}
                          className="spinner"
                          style={{ animation: "spin 2s linear infinite" }}
                        />
                        <span>{t("guestCheckout.sendingLink")}</span>
                      </>
                    ) : (
                      <>
                        <Mail size={18} />
                        <span>{t("guestCheckout.sendSetupLinkBtn")}</span>
                      </>
                    )}
                  </button>

                  <span className="guest-account-hint">
                    <ShieldCheck size={14} />
                    <span>{t("guestCheckout.fastSetupBadge")}</span>
                  </span>

                  {setupLinkError && (
                    <div className="auth-error" role="alert" style={{ marginTop: "14px", width: "100%" }}>
                      <AlertCircle size={15} />
                      <span>{setupLinkError}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="guest-link-sent-box">
                  <div className="guest-link-sent-icon">
                    <CheckCircle2 size={36} />
                  </div>
                  <span className="guest-account-header__badge" style={{ marginBottom: "10px" }}>
                    <Check size={13} />
                    <span>{t("guestCheckout.linkSentBadge")}</span>
                  </span>
                  <h3 className="guest-link-sent-title">{t("guestCheckout.linkSentTitle")}</h3>
                  <p className="guest-link-sent-desc">
                    {t("guestCheckout.linkSentDesc").replace("{email}", orderPlacedInfo.email)}
                  </p>

                  <div className="guest-pwd-tip">
                    <AlertCircle size={15} />
                    <span>{t("guestCheckout.linkSentSpamNotice")}</span>
                  </div>

                  <div className="guest-resend-row">
                    <span className="guest-resend-prompt">{t("guestCheckout.resendPrompt")}</span>
                    <button
                      type="button"
                      className="btn-outline guest-resend-btn"
                      onClick={handleRequestSetupLink}
                      disabled={setupLinkLoading || setupLinkCooldown > 0}
                    >
                      <RotateCcw size={14} className={setupLinkLoading ? "spinner" : ""} />
                      <span>
                        {setupLinkCooldown > 0
                          ? t("guestCheckout.resendCooldown").replace("{seconds}", setupLinkCooldown)
                          : t("guestCheckout.resendBtn")}
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Alternative Reset Password Info Tab / Dropdown */}
              <div className="guest-reset-info-card">
                <button
                  type="button"
                  className="guest-reset-info-toggle"
                  onClick={() => setIsResetInfoExpanded((prev) => !prev)}
                  aria-expanded={isResetInfoExpanded}
                >
                  <span className="guest-reset-info-toggle__title">
                    <HelpCircle size={16} />
                    <span>{t("guestCheckout.resetFeatureTabTitle")}</span>
                  </span>
                  <ChevronDown
                    size={16}
                    className={`guest-account-toggle-btn__chevron ${
                      isResetInfoExpanded ? "guest-account-toggle-btn__chevron--open" : ""
                    }`}
                  />
                </button>

                {isResetInfoExpanded && (
                  <div className="guest-reset-info-content">
                    <p>
                      {t("guestCheckout.resetFeatureTip1")}{" "}
                      <strong>{t("guestCheckout.resetFeatureTipHighlight")}</strong>{" "}
                      {t("guestCheckout.resetFeatureTip2")
                        .replace("{email}", orderPlacedInfo.email)
                        .replace("{orderId}", String(orderPlacedInfo.orderId || ""))}
                    </p>
                    <Link to="/login" className="guest-reset-info-link">
                      <ExternalLink size={14} />
                      <span>{t("guestCheckout.goToLogin")}</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* 4. Action Strip */}
          <div className="thanks-actions" style={{ marginTop: "8px" }}>
            <button
              type="button"
              className="btn-primary thanks-btn-primary"
              onClick={() => navigate("/shop")}
            >
              <ShoppingBag size={17} />
              <span>{t("guestCheckout.continueShopping")}</span>
              <ArrowRight size={16} className="rtl-flip" />
            </button>
          </div>

          {/* 5. Concierge & Support Banner */}
          <div className="thanks-help-banner">
            <HelpCircle size={18} className="thanks-help-banner__icon" />
            <div className="thanks-help-banner__text">
              <span>{t("guestCheckout.needHelp")}</span>
              <Link to="/contact" className="thanks-help-banner__link">
                {t("guestCheckout.contactSupport")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EMPTY CART STATE
  if (guestCart.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container checkout-inner">
          <div className="checkout-empty-banner">
            <h2 className="checkout-header__title" style={{ fontSize: "1.8rem" }}>
              {t("guestCheckout.emptyTitle")}
            </h2>
            <p>{t("guestCheckout.emptyDesc")}</p>
            <Link to="/shop" className="btn-primary">
              <span>{t("guestCheckout.exploreCollections")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </Link>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="checkout-page">
      <div className="container checkout-inner">
        {/* Checkout Step Breadcrumbs matching Cart and Checkout */}
        <nav className="cart-steps" aria-label="Checkout flow">
          <Link
            to="/cart"
            className="cart-step cart-step--completed"
            title={t("checkout.backToCart")}
          >
            <span className="cart-step__num">
              <Check size={13} strokeWidth={2.5} />
            </span>
            <span className="cart-step__label">{t("cart.stepBag")}</span>
          </Link>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--active" aria-current="step">
            <span className="cart-step__num">2</span>
            <span className="cart-step__label">{t("cart.stepCheckout")}</span>
          </div>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--inactive">
            <span className="cart-step__num">3</span>
            <span className="cart-step__label">{t("cart.stepDone")}</span>
          </div>
        </nav>

        <div className="checkout-header">
          <div className="checkout-header__title-group">
            <h1 className="checkout-header__title">{t("guestCheckout.title")}</h1>
            <p className="checkout-header__subtitle">{t("guestCheckout.subtitle")}</p>
          </div>
          <p className="checkout-header__guest-switch">
            <span>{t("guestCheckout.haveAccount")} </span>
            <Link to="/login" state={{ from: { pathname: "/checkout" } }}>
              {t("guestCheckout.signIn")}
            </Link>
          </p>
        </div>

        {isOverPackageLimit && (
          <div className="cart-package-limit-alert" role="alert" style={{ marginBottom: "24px" }}>
            <div className="cart-package-limit-alert__icon">
              <AlertCircle size={22} />
            </div>
            <div className="cart-package-limit-alert__content">
              <h3 className="cart-package-limit-alert__title">
                {t("cart.packageLimitExceededBannerTitle")}
              </h3>
              <p className="cart-package-limit-alert__desc">
                {t("cart.packageLimitExceededBannerDesc").replace(
                  "{maxVolume}",
                  maxPackageVolume.toLocaleString()
                )}
              </p>
              <div style={{ marginTop: "12px" }}>
                <Link
                  to="/cart"
                  className="btn-outline"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "7px 16px",
                    fontSize: "13px",
                    textDecoration: "none",
                  }}
                >
                  <ShoppingBag size={14} />
                  <span>{t("cart.title")}</span>
                  {isRTL ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
                </Link>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="checkout-layout">
            {/* Form Fields Left Column */}
            <div className="checkout-main-col">
              {/* CONTACT SECTION */}
              <section className="checkout-card">
                <div className="checkout-card__head">
                  <div className="checkout-card__icon-wrap">
                    <User size={18} />
                  </div>
                  <h2 className="checkout-card__title">
                    {t("guestCheckout.contactInfo")}
                  </h2>
                </div>
                <p className="checkout-card__sub">
                  {t("guestCheckout.contactSub")}
                </p>

                <div className="auth-field">
                  <label>{t("guestCheckout.email")}</label>
                  <input
                    type="email"
                    placeholder={t("guestCheckout.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-two-col">
                  <div className="auth-field">
                    <label>{t("guestCheckout.firstName")}</label>
                    <input
                      type="text"
                      placeholder={t("guestCheckout.firstNamePlaceholder")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="auth-field">
                    <label>{t("guestCheckout.lastName")}</label>
                    <input
                      type="text"
                      placeholder={t("guestCheckout.lastNamePlaceholder")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </section>

              {/* SHIPPING ADDRESS SECTION */}
              <section className="checkout-card">
                <div className="checkout-card__head">
                  <div className="checkout-card__icon-wrap">
                    <MapPin size={18} />
                  </div>
                  <h2 className="checkout-card__title">
                    {t("guestCheckout.deliveryAddress")}
                  </h2>
                </div>

                <div className="auth-field">
                  <label>{t("guestCheckout.country")}</label>
                  <input
                    type="text"
                    value={isRTL ? "مصر (Egypt)" : "Egypt"}
                    disabled
                    readOnly
                    className="checkout-readonly-input"
                  />
                </div>

                <div className="form-two-col">
                  <div className="auth-field">
                    <label>{t("guestCheckout.city")}</label>
                    <select
                      className="checkout-select"
                      value={cityId}
                      onChange={(e) => {
                        const newCity = e.target.value;
                        setCityId(newCity);
                        setDistrictId("");
                        setDistricts([]);
                        setCheckoutSummary(null);
                      }}
                      disabled={loadingCities}
                      required
                    >
                      <option value="" disabled>
                        {loadingCities
                          ? t("guestCheckout.loadingCities")
                          : t("guestCheckout.selectCity")}
                      </option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {isRTL && c.nameAr
                            ? `${c.nameAr} (${c.name})`
                            : `${c.name} ${c.nameAr ? `(${c.nameAr})` : ""}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="auth-field">
                    <label>{t("guestCheckout.district")}</label>
                    <SearchableSelect
                      options={districts}
                      value={districtId}
                      onChange={(val) => setDistrictId(val)}
                      placeholder={
                        !cityId
                          ? t("guestCheckout.selectCityFirst")
                          : loadingDistricts
                          ? t("guestCheckout.loadingDistricts")
                          : t("guestCheckout.selectDistrict")
                      }
                      disabled={!cityId || loadingDistricts || districts.length === 0}
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ margin: 0 }}>{t("guestCheckout.streetDetails")}</label>
                    <span
                      className={`checkout-char-count ${
                        description.trim().length >= 25
                          ? "checkout-char-count--valid"
                          : "checkout-char-count--invalid"
                      }`}
                    >
                      {t("guestCheckout.minCharsNotice").replace(
                        "{count}",
                        description.trim().length
                      )}
                    </span>
                  </div>
                  <textarea
                    rows={3}
                    placeholder={t("guestCheckout.streetPlaceholder")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={`checkout-textarea ${
                      description.trim().length > 0 && description.trim().length < 25
                        ? "checkout-textarea--error"
                        : ""
                    }`}
                    required
                  />
                  <span className="checkout-field-hint">
                    {t("guestCheckout.streetHint")}
                  </span>
                </div>

                <div className="auth-field">
                  <label>{t("guestCheckout.phone")}</label>
                  <div className="checkout-phone-group">
                    <div className="checkout-phone-prefix">+20</div>
                    <input
                      type="tel"
                      className="checkout-phone-input"
                      placeholder={t("guestCheckout.phonePlaceholder")}
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      dir="ltr"
                      required
                    />
                  </div>
                </div>
              </section>

              {/* PAYMENT SECTION */}
              <section className="checkout-card">
                <div className="checkout-card__head">
                  <div className="checkout-card__icon-wrap">
                    <Banknote size={18} />
                  </div>
                  <h2 className="checkout-card__title">
                    {t("guestCheckout.paymentMethod")}
                  </h2>
                </div>

                <div
                  className="checkout-payment-grid"
                  role="radiogroup"
                  aria-label={t("guestCheckout.paymentMethod")}
                >
                  <div
                    role="radio"
                    aria-checked={paymentMethod === "CashOnDelivery"}
                    tabIndex={0}
                    className={`checkout-payment-card ${
                      paymentMethod === "CashOnDelivery"
                        ? "checkout-payment-card--selected"
                        : ""
                    }`}
                    onClick={() => setPaymentMethod("CashOnDelivery")}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setPaymentMethod("CashOnDelivery");
                      }
                    }}
                  >
                    <div className="checkout-payment-card__radio">
                      <span
                        className={`checkout-radio-dot ${
                          paymentMethod === "CashOnDelivery"
                            ? "checkout-radio-dot--active"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="checkout-payment-card__icon-wrap">
                      <Banknote size={22} />
                    </div>
                    <div className="checkout-payment-card__content">
                      <div className="checkout-payment-card__title-row">
                        <span className="checkout-payment-card__title">
                          {t("guestCheckout.cod")}
                        </span>
                        <span className="checkout-payment-card__badge">
                          {isRTL ? "متاح في مصر" : "Egypt"}
                        </span>
                      </div>
                      <p className="checkout-payment-card__sub">
                        {t("guestCheckout.codSub")}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Order Summary Aside */}
            <aside className="checkout-aside">
              <div className="checkout-aside__header">
                <h3 className="checkout-aside__title">
                  {t("guestCheckout.orderSummary")}
                </h3>
                <span className="checkout-aside__badge">
                  {guestCart.length}{" "}
                  {isRTL
                    ? guestCart.length === 1
                      ? "قطعة"
                      : "قطع"
                    : guestCart.length === 1
                    ? "item"
                    : "items"}
                </span>
              </div>

              <ul className="checkout-lines">
                {guestCart.map((item) => {
                  const thumb = item.imageUrl ? resolveImageUrl(item.imageUrl) : null;
                  const unitPrice = Number(item.price) || 0;
                  const qty = Number(item.quantity ?? item.qty ?? 1);
                  const lineTotal = unitPrice * qty;
                  const formattedSize = item.sizeName
                    ? formatProductSize(item.sizeName, language)
                    : null;
                  const variantText = [formattedSize, item.colorName]
                    .filter(Boolean)
                    .join(" · ");
                  const title =
                    isRTL && item.arabicName
                      ? item.arabicName
                      : item.name || (isRTL ? "منتج" : "Product");

                  return (
                    <li key={item.id} className="checkout-line">
                      <div
                        className="checkout-line__thumb"
                        style={
                          thumb
                            ? { backgroundImage: `url(${thumb})` }
                            : { backgroundColor: item.colorHex || "var(--cream)" }
                        }
                      />
                      <div className="checkout-line__details">
                        <span className="checkout-line__name">{title}</span>
                        {variantText && (
                          <span className="checkout-line__variant">
                            {variantText}
                          </span>
                        )}
                        <div className="checkout-line__footer">
                          <span className="checkout-line__qty">×{qty}</span>
                          <span className="checkout-line__price">
                            {formatMoney(lineTotal, language)}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="checkout-breakdown">
                <div className="checkout-breakdown__row">
                  <span>{t("guestCheckout.subtotal")}</span>
                  <span>{formatMoney(currentSubtotal, language)}</span>
                </div>
                <div className="checkout-breakdown__row">
                  <span className="checkout-breakdown__shipping-label">
                    <Truck size={14} />
                    <span>{t("guestCheckout.shipping")}</span>
                  </span>
                  <span>
                    {loadingSummary ? (
                      <span className="checkout-shipping-calc">
                        <Loader2 size={13} className="spinner" />
                        <span>{t("guestCheckout.calculating")}</span>
                      </span>
                    ) : calculatedShipping != null ? (
                      calculatedShipping === 0 ? (
                        <span className="checkout-free-shipping">
                          {t("guestCheckout.freeShipping")}
                        </span>
                      ) : (
                        <strong>{formatMoney(calculatedShipping, language)}</strong>
                      )
                    ) : districtId ? (
                      <span className="checkout-shipping-unavailable">
                        {t("guestCheckout.shippingUnavailable")}
                      </span>
                    ) : (
                      <span className="checkout-shipping-hint">
                        {t("guestCheckout.selectDistrictHint")}
                      </span>
                    )}
                  </span>
                </div>

                {!districtId && (
                  <p className="checkout-district-prompt">
                    {t("guestCheckout.calcPrompt")}
                  </p>
                )}

                {appliedCoupon && effectiveDiscount > 0 && (
                  <div className="checkout-breakdown__row checkout-breakdown__row--discount">
                    <span className="checkout-breakdown__discount-label">
                      <span>{t("coupon.discount")}</span>
                      <span className="checkout-breakdown__coupon-code">({appliedCoupon.code})</span>
                    </span>
                    <span className="checkout-breakdown__discount-val">
                      -{formatMoney(effectiveDiscount, language)}
                    </span>
                  </div>
                )}

                <div className="checkout-breakdown__total-row">
                  <span>{t("guestCheckout.totalDue")}</span>
                  <strong>{formatMoney(currentTotal, language)}</strong>
                </div>
              </div>

              <div className="checkout-coupon-section" style={{ marginTop: "16px", marginBottom: "16px" }}>
                <CouponBox customCartTotal={currentSubtotal} />
              </div>

              {isOverPackageLimit && (
                <div className="cart-summary__limit-warning" role="alert" style={{ marginBottom: "14px" }}>
                  <AlertCircle size={15} />
                  <span>{t("cart.mustReduceItemsToCheckout")}</span>
                </div>
              )}

              <button
                type="submit"
                className={`btn-primary checkout-pay-btn ${isOverPackageLimit ? "cart-summary__checkout-btn--disabled" : ""}`}
                disabled={submitting || isOverPackageLimit}
                title={isOverPackageLimit ? t("cart.mustReduceItemsToCheckout") : undefined}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="spinner" />
                    <span>{t("guestCheckout.placingOrder")}</span>
                  </>
                ) : (
                  <>
                    <Lock size={15} />
                    <span>{t("guestCheckout.placeOrder")}</span>
                    <ArrowRight size={14} className="rtl-flip" />
                  </>
                )}
              </button>

              {/* Trust Badges */}
              <div className="checkout-trust-box">
                <div className="checkout-trust-item">
                  <Truck size={16} className="checkout-trust-icon" />
                  <span>{t("guestCheckout.trustShipping")}</span>
                </div>
                <div className="checkout-trust-item">
                  <ShieldCheck size={16} className="checkout-trust-icon" />
                  <span>{t("guestCheckout.trustCod")}</span>
                </div>
                <div className="checkout-trust-item">
                  <RotateCcw size={16} className="checkout-trust-icon" />
                  <span>{t("guestCheckout.trustReturns")}</span>
                </div>
              </div>

              <Link to="/cart" className="checkout-back-link">
                {isRTL ? <ArrowRight size={13} /> : <ArrowLeft size={13} />}
                <span>{t("guestCheckout.backToCart")}</span>
              </Link>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}
